import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { useStripeContext } from '../../contexts/StripeContext';
import { paymentService } from '../../services/payments';
import StripePaymentFormInner from './StripePaymentFormInner';
import AppIcon from '../AppIcon';
import Button from '../ui/Button';

const EnquiryAcceptancePayment = ({
  enquiry,
  agentInfo,
  onSuccess,
  onCancel,
  isOpen
}) => {
  const { stripePromise, stripeOptions } = useStripeContext();
  const [clientSecret, setClientSecret] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Payment configuration - £7.00 per enquiry acceptance
  const ENQUIRY_ACCEPTANCE_FEE = 7.00; // in GBP
  const CURRENCY = 'GBP';

  useEffect(() => {
    if (isOpen && enquiry && agentInfo) {
      initializePayment();
    }
  }, [isOpen, enquiry, agentInfo]);

  const initializePayment = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Prepare order data for enquiry acceptance payment
      const orderData = {
        enquiry_id: enquiry?.id,
        items: [{
          name: `Enquiry Access Fee - ${enquiry?.destination || enquiry?.title}`,
          traveler_count: 1,
          unit_price: ENQUIRY_ACCEPTANCE_FEE,
          total_price: ENQUIRY_ACCEPTANCE_FEE,
          package_id: null,
          variant_id: null
        }],
        subtotal: ENQUIRY_ACCEPTANCE_FEE,
        tax_amount: 0,
        service_fee: 0,
        discount_amount: 0,
        total: ENQUIRY_ACCEPTANCE_FEE,
        currency: CURRENCY,
        destination: enquiry?.destination,
        special_requests: `Agent ${agentInfo?.full_name} accepting enquiry ${enquiry?.id}`
      };

      // Prepare customer info (agent is the customer for this transaction)
      const customerInfo = {
        userId: agentInfo?.id,
        firstName: agentInfo?.full_name?.split(' ')?.[0] || 'Agent',
        lastName: agentInfo?.full_name?.split(' ')?.slice(1)?.join(' ') || 'User',
        email: agentInfo?.email,
        phone: agentInfo?.phone || '',
        stripeCustomerId: agentInfo?.stripe_customer_id || null,
        billing: {
          address_line_1: agentInfo?.business_address || '123 Business Street',
          address_line_2: '',
          city: agentInfo?.location || 'London',
          state: 'England',
          postal_code: 'SW1A 1AA',
          country: 'GB'
        }
      };

      // Create payment intent
      const paymentData = await paymentService?.createPaymentIntent(orderData, customerInfo);

      if (paymentData?.clientSecret) {
        setClientSecret(paymentData?.clientSecret);
      } else {
        throw new Error('Failed to initialize payment');
      }
    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(err?.message || 'Failed to initialize payment. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = (result) => {
    console.log('Payment successful:', result);
    onSuccess?.(result);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
    setError(error?.message || 'Payment failed. Please try again.');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Accept Enquiry Payment</h2>
            <p className="text-sm text-gray-600 mt-1">
              Pay £{ENQUIRY_ACCEPTANCE_FEE?.toFixed(2)} to accept this enquiry
            </p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <AppIcon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Enquiry Details */}
          <div className="bg-blue-50 rounded-lg p-4 space-y-3">
            <div className="flex items-start space-x-3">
              <AppIcon name="MapPin" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{enquiry?.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{enquiry?.destination}</p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4 pt-3 border-t border-blue-100">
              <div className="flex items-center space-x-2">
                <AppIcon name="Calendar" size={16} className="text-blue-600" />
                <span className="text-sm text-gray-700">
                  {enquiry?.departure_date ? new Date(enquiry?.departure_date)?.toLocaleDateString() : 'Flexible dates'}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <AppIcon name="Users" size={16} className="text-blue-600" />
                <span className="text-sm text-gray-700">
                  {enquiry?.adults || 1} adults, {enquiry?.children || 0} children
                </span>
              </div>
            </div>
          </div>

          {/* Payment Info */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start space-x-3">
            <AppIcon name="Info" size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-yellow-900 font-medium">Enquiry Access Fee</p>
              <p className="text-sm text-yellow-800 mt-1">
                A one-time fee of £{ENQUIRY_ACCEPTANCE_FEE?.toFixed(2)} is charged when you accept an enquiry.
                This grants you exclusive access to communicate with the customer and submit your quote.
              </p>
            </div>
          </div>

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
              <AppIcon name="AlertCircle" size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-800 text-sm font-medium">Error</p>
                <p className="text-red-700 text-sm mt-1">{error}</p>
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="ml-4 text-gray-600">Initializing secure payment...</p>
            </div>
          )}

          {/* Payment Form */}
          {!isLoading && clientSecret && (
            <Elements stripe={stripePromise} options={{
              clientSecret,
              ...stripeOptions,
              defaultValues: {
                billingDetails: {
                  name: agentInfo?.full_name || '',
                  email: agentInfo?.email || '',
                  phone: agentInfo?.phone || ''
                }
              }
            }}>
              <StripePaymentFormInner
                clientSecret={clientSecret}
                amount={ENQUIRY_ACCEPTANCE_FEE * 100} // Convert to pence
                currency={CURRENCY}
                orderData={{
                  orderId: null,
                  orderNumber: `ENQ-${enquiry?.id?.slice(0, 8)}`,
                  items: [{
                    name: `Enquiry Access - ${enquiry?.destination || enquiry?.title}`,
                    traveler_count: 1,
                    total_price: ENQUIRY_ACCEPTANCE_FEE
                  }]
                }}
                customerInfo={{
                  firstName: agentInfo?.full_name?.split(' ')?.[0] || 'Agent',
                  lastName: agentInfo?.full_name?.split(' ')?.slice(1)?.join(' ') || 'User',
                  email: agentInfo?.email
                }}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                confirmButtonText="Pay £7.00 & Accept Enquiry"
                className=""
              />
            </Elements>
          )}

          {/* Retry Button */}
          {!isLoading && !clientSecret && error && (
            <Button
              onClick={initializePayment}
              className="w-full"
              iconName="RefreshCw"
              iconPosition="left"
            >
              Retry Payment Setup
            </Button>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <div className="flex items-center space-x-2">
              <AppIcon name="Shield" size={16} className="text-green-600" />
              <span>Secured by Stripe</span>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-600 hover:text-gray-900 font-medium"
              disabled={isLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnquiryAcceptancePayment;